/**
 * Campagna Mirtilleto Natale 2026.
 * Modulo ES senza dipendenze: convive con jQuery e Bootstrap Italia.
 * Figma: https://www.figma.com/design/Z8OrxHIgmj8xnEYRYU1w7m
 *
 * Regole funzionali:
 * - badge sempre disponibile (visibile anche prima dei 7 secondi);
 * - prima apertura automatica, da qualunque pagina di atterraggio, dopo 7 s;
 * - una sola apertura automatica per sessione/tab, anche attraverso altre pagine;
 * - chiusura => sessionStorage.modal_closed_permanently = 'true';
 * - badge sempre riapribile manualmente anche dopo la chiusura;
 * - reinizializzazione idempotente in caso di aggiornamenti AJAX.
 */

const STORAGE = {
	arrived: 'mirtilleto_christmas_first_arrival',
	autoShown: 'mirtilleto_christmas_auto_shown',
	permanentlyClosed: 'modal_closed_permanently'
};

let activeController = null;

function safeSessionGet(key) {
	try { return window.sessionStorage.getItem(key); }
	catch (_) { return null; }
}
function safeSessionSet(key, value) {
	try { window.sessionStorage.setItem(key, value); }
	catch (_) { /* Storage disabilitato: resta funzionante nella pagina corrente. */ }
}

function createController(root, overlay, launcher) {
	const panel = overlay.querySelector('.mirtilleto-campaign__panel');
	const closeButton = overlay.querySelector('[data-mirtilleto-close]');
	if (!panel || !closeButton) return null;

	const requestedDelay = Number(root.getAttribute('data-mirtilleto-delay'));
	const delay = Number.isFinite(requestedDelay) && requestedDelay >= 0 ? requestedDelay : 7000;
	const body = document.body;
	let timerId = null;
	let previousFocus = null;
	let previousBodyOverflow = '';
	let previousBodyPaddingRight = '';
	let bodyWasLocked = false;
	let destroyed = false;

	const isOpen = () => !overlay.hidden;

	function cancelTimer() {
		if (timerId !== null) {
			window.clearTimeout(timerId);
			timerId = null;
		}
	}

	function lockPageScroll() {
		if (bodyWasLocked) return;
		previousBodyOverflow = body.style.overflow;
		previousBodyPaddingRight = body.style.paddingRight;
		// Compensa la scomparsa della scrollbar senza spostare l'header.
		const scrollbar = window.innerWidth - document.documentElement.clientWidth;
		const currentPadding = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
		body.style.overflow = 'hidden';
		if (scrollbar > 0) body.style.paddingRight = `${currentPadding + scrollbar}px`;
		bodyWasLocked = true;
	}

	function unlockPageScroll() {
		if (!bodyWasLocked) return;
		body.style.overflow = previousBodyOverflow;
		body.style.paddingRight = previousBodyPaddingRight;
		bodyWasLocked = false;
	}

	function getFocusable() {
		return Array.from(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
			.filter(el => el.getClientRects().length > 0 && !el.hidden);
	}

	function keyHandler(event) {
		if (!isOpen()) return;
		if (event.key === 'Escape') {
			event.preventDefault();
			close();
			return;
		}
		if (event.key !== 'Tab') return;
		const focusable = getFocusable();
		if (!focusable.length) {
			event.preventDefault();
			panel.focus({ preventScroll: true });
			return;
		}
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
			event.preventDefault();
			first.focus();
		}
	}

	function open() {
		if (destroyed || isOpen()) return;
		cancelTimer();
		safeSessionSet(STORAGE.autoShown, 'true');
		previousFocus = document.activeElement;
		overlay.hidden = false;
		root.classList.add('is-open');
		launcher.inert = true;
		overlay.removeAttribute('aria-hidden');
		launcher.setAttribute('aria-expanded', 'true');
		lockPageScroll();
		document.addEventListener('keydown', keyHandler, true);
		closeButton.focus({ preventScroll: true });
	}

	function close() {
		if (destroyed || !isOpen()) return;
		safeSessionSet(STORAGE.permanentlyClosed, 'true');
		cancelTimer();
		overlay.hidden = true;
		root.classList.remove('is-open');
		launcher.inert = false;
		overlay.setAttribute('aria-hidden', 'true');
		launcher.setAttribute('aria-expanded', 'false');
		document.removeEventListener('keydown', keyHandler, true);
		unlockPageScroll();
		// Il badge rimane nel flusso di lettura ed è il controllo per riaprire.
		if (launcher.isConnected) launcher.focus({ preventScroll: true });
		else if (previousFocus && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
	}

	function clickLauncher(event) {
		event.preventDefault();
		open();
	}
	function clickClose(event) {
		event.preventDefault();
		close();
	}
	function clickBackdrop(event) {
		if (event.target === overlay) close();
	}

	launcher.addEventListener('click', clickLauncher);
	closeButton.addEventListener('click', clickClose);
	overlay.addEventListener('click', clickBackdrop);
	launcher.setAttribute('aria-expanded', 'false');
	overlay.hidden = true;
	overlay.setAttribute('aria-hidden', 'true');

	// Timer ancorato al primo atterraggio nella sessione, non a ogni reload.
	// Un cambio pagina entro i 7 secondi non fa ripartire il conteggio.
	if (safeSessionGet(STORAGE.permanentlyClosed) !== 'true' && safeSessionGet(STORAGE.autoShown) !== 'true') {
		const previousTimestamp = Number(safeSessionGet(STORAGE.arrived));
		const arrived = Number.isFinite(previousTimestamp) && previousTimestamp > 0
			? previousTimestamp
			: Date.now();
		if (!previousTimestamp) safeSessionSet(STORAGE.arrived, String(arrived));
		timerId = window.setTimeout(() => {
			timerId = null;
			if (safeSessionGet(STORAGE.permanentlyClosed) !== 'true' && safeSessionGet(STORAGE.autoShown) !== 'true') {
				open();
			}
		}, Math.max(0, delay - (Date.now() - arrived)));
	}

	function destroy() {
		if (destroyed) return;
		cancelTimer();
		document.removeEventListener('keydown', keyHandler, true);
		launcher.removeEventListener('click', clickLauncher);
		closeButton.removeEventListener('click', clickClose);
		overlay.removeEventListener('click', clickBackdrop);
		unlockPageScroll();
		root.classList.remove('is-open');
		launcher.inert = false;
		destroyed = true;
	}

	return { root, overlay, launcher, open, close, destroy, isOpen };
}

/**
 * Chiamare dopo DOM ready e dopo sostituzioni AJAX che toccano il layout.
 * Non crea mai listener/timer doppi per gli stessi elementi.
 */
export function initMirtilletoChristmas() {
	const root = document.querySelector('[data-mirtilleto]');
	const overlay = root && root.querySelector('[data-mirtilleto-overlay]');
	const launcher = root && root.querySelector('[data-mirtilleto-open]');
	if (!root || !overlay || !launcher) {
		if (activeController && !activeController.root.isConnected) {
			activeController.destroy();
			activeController = null;
		}
		return null;
	}
	if (activeController && activeController.root === root && activeController.overlay === overlay && activeController.launcher === launcher) {
		return activeController;
	}
	if (activeController) activeController.destroy();
	activeController = createController(root, overlay, launcher);
	return activeController;
}
